import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Icon, { iconNames } from "../../icons";
import { getMenuDisplayName, isMenuActive, MenuItemData } from "./MenuItem";

interface FloatingSubmenuProps {
  menu: MenuItemData;
  anchorTop: number;
  sidebarWidth: number;
  onClose: () => void;
  onNavigate: (url: string) => void;
  DISABLED_MENU_ITEMS: string[];
  DELETED_MENU_ITEMS: string[];
  pathname: string;
}

const PANEL_W = 210;

const FloatingSubmenu: React.FC<FloatingSubmenuProps> = ({
  menu,
  anchorTop,
  sidebarWidth,
  onClose,
  onNavigate,
  DISABLED_MENU_ITEMS,
  DELETED_MENU_ITEMS,
  pathname,
}) => {
  const { t } = useTranslation("menu");
  const panelRef = useRef<HTMLDivElement>(null);

  /* Close on outside click */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const items = (menu.submenu ?? []).filter(
    (item) => !DELETED_MENU_ITEMS.includes(item.menuCode)
  );

  const clampedTop = Math.min(
    anchorTop,
    window.innerHeight - Math.min(items.length * 38 + 44, 380)
  );

  const navigate = (url: string) => {
    onNavigate(url);
    onClose();
  };

  return (
    <div
      ref={panelRef}
      style={{
        position: "fixed",
        left: sidebarWidth,
        top: clampedTop,
        width: PANEL_W,
        zIndex: 1000,
      }}
      className="bg-white shadow-xl border border-gray-200 rounded-r-lg overflow-hidden"
    >
      {/* Header */}
      <div className="bg-gray-100 px-3 py-2 flex items-center gap-2 border-b border-gray-200">
        {menu.icon && (
          <Icon name={menu.icon as iconNames} size={14} color="#6B7280" />
        )}
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          {getMenuDisplayName(menu, t)}
        </span>
      </div>

      {/* Items */}
      <div className="max-h-[340px] overflow-y-auto">
        {items.map((item) => {
          const isDisabled =
            (!item.read && !item.write && !item.delete) ||
            DISABLED_MENU_ITEMS.includes(item.menuCode);
          const isActive = isMenuActive(item, pathname);

          return (
            <button
              key={item.menuId}
              disabled={isDisabled}
              onClick={() => {
                if (item.url && item.url !== "N/A" && item.url !== "#") {
                  navigate(item.url);
                }
              }}
              className={[
                "w-full flex items-center gap-2 px-3 py-2 text-sm text-left",
                isDisabled
                  ? "opacity-40 cursor-not-allowed"
                  : "cursor-pointer hover:bg-gray-50",
                isActive ? "text-secondary font-medium bg-secondary/10" : "text-gray-700",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <span className="flex-grow">{getMenuDisplayName(item, t)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FloatingSubmenu;
